import React, { useState, useEffect, useMemo } from 'react';
import { Server } from 'lucide-react';
import { apiUrl } from '../config';
import ServiceAccountSection from './ServiceAccountSection';
import SharedCreateCompanyModal from './SharedCreateCompanyModal';

interface Company {
  companyId: string;
  companyName: string;
  companyUrl: string;
  companyState: string;
  primaryContact?: string;
  primaryPhone?: string;
  primaryEmail?: string;
  address?: string;
  allowedIpAddresses?: string;
  allowAllIps?: boolean;
  enableInboundNotifications?: boolean;
  enableInboundDownloads?: boolean;
  serviceAccount?: {
    username: string;
    email: string;
    role: string;
    createdAt: string;
  };
}

interface EditCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  company: Company | null;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onReload: () => Promise<void>;
}

export default function EditCompanyModal({
  isOpen,
  onClose,
  company,
  onSuccess,
  onError,
  onReload,
}: EditCompanyModalProps) {
  const [loading, setLoading] = useState(false);
  const [companyWithServiceAccount, setCompanyWithServiceAccount] = useState<Company | null>(null);

  // Use company data directly - it should already have service account info from the list API
  useEffect(() => {
    if (isOpen && company) {
      console.log('🔍 Using company data directly:', company);
      setCompanyWithServiceAccount(company);
    }
  }, [isOpen, company]);

  const handleServiceAccountPasswordReset = async (username: string) => {
    if (!company) return;

    try {
      const res = await fetch(`${apiUrl('users')}/${encodeURIComponent(username)}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          passwordOption: 'generated',
        }),
      });

      if (!res.ok) throw new Error(await res.text());

      const result = await res.json();
      onSuccess(
        `✅ Service account password reset successfully${result.password ? ` - New password: ${result.password}` : ''}`
      );
    } catch (err: any) {
      onError(`❌ Failed to reset service account password: ${err.message}`);
    }
  };

  const handleServiceAccountDelete = async (username: string) => {
    if (!company) return;

    // TODO: Replace with proper confirmation dialog
    // if (!confirm(`Are you sure you want to delete the service account "${username}"?`)) return;

    try {
      const res = await fetch(`${apiUrl('users')}/${encodeURIComponent(username)}`, {
        method: 'DELETE',
      });

      if (!res.ok) throw new Error(await res.text());

      onSuccess('✅ Service account deleted successfully');
      await onReload();
    } catch (err: any) {
      onError(`❌ Failed to delete service account: ${err.message}`);
    }
  };

  const handleClose = () => {
    setLoading(false);
    onClose();
  };

  if (!isOpen || !company) return null;

  // Always use the most recent company data, not the cached version
  const companyToUse = company;
  console.log('🔍 EditCompanyModal - Company data received:', company);
  console.log('🔍 EditCompanyModal - Company with service account:', companyWithServiceAccount);
  console.log('🔍 EditCompanyModal - Service account data:', companyToUse?.serviceAccount);

  // Create initialData without useMemo to avoid dependency issues
  const initialData = (() => {
    if (!companyToUse) return {};

    const data = {
      companyName: companyToUse.companyName || '',
      companyUrl: companyToUse.companyUrl || '',
      companyState: companyToUse.companyState || '',
      primaryContact: companyToUse.primaryContact || '',
      primaryPhone: companyToUse.primaryPhone || '',
      primaryEmail: companyToUse.primaryEmail || '',
      address: companyToUse.address || '',
      allowedIpAddresses: companyToUse.allowedIpAddresses || '',
      allowAllIps: companyToUse.allowAllIps || false,
      enableInboundNotifications: companyToUse.enableInboundNotifications ?? false,
      enableInboundDownloads: companyToUse.enableInboundDownloads ?? false,
      serviceAccount: companyToUse.serviceAccount || null,
    };
    console.log('🔍 EditCompanyModal - Creating initialData:', JSON.stringify(data, null, 2));
    return data;
  })();

  return (
    <SharedCreateCompanyModal
      key={companyToUse?.companyId || 'new'} // Force remount when company changes
      isOpen={isOpen}
      onClose={handleClose}
      onCompanyCreated={async updatedCompany => {
        onSuccess('✅ Company updated successfully');
        // Add a small delay to ensure the backend has processed the update
        setTimeout(async () => {
          await onReload();
        }, 500);
      }}
      includeServiceAccount={true}
      initialData={initialData}
      mode='edit'
      companyId={companyToUse?.companyId}
    />
  );
}
